import { useCallback } from 'react';

import { mapUnitOptions } from './mapUnitOptions';
import {
  type UnitLabelType,
  type UnitsLanguage,
  type UnitsListItem,
  type UnitsResponse,
  type UnitType,
} from './types';
import { useQueryUnits } from './useQueryUnits';

type P = {
  language?: UnitsLanguage;
  label?: UnitLabelType | 'combined';
  filterFunction?: (entry: [string, UnitsListItem]) => boolean;
  unitType?: UnitType | UnitType[];
};

type UnitSelectOption = ReturnType<typeof mapUnitOptions>[number];

/** Referentially stable, so the default keeps `select` below memoizable. */
const includeAllUnits = () => true;

/**
 * Syntactic sugar for getting UN/CEFACT unit options for a select component using the useQueryUnits hook.
 * @param params - The parameters for the hook.
 * @param params.filterFunction - A function to filter the returned units client-side (defaults to returning all units).
 * @param params.label - The long or short version of the translation to return (defaults to 'fullName').
 * @param params.language - The language to fetch the units in (defaults to the active UI language).
 * @param params.unitType - A list of unit types to filter the requested units server-side (defaults to all types).
 * @returns The query result containing a list of options for a select component.
 */
export const useUnitOptions = ({
  filterFunction = includeAllUnits,
  label = 'fullName',
  language,
  unitType,
}: P) => {
  // Reference stability matters: TanStack Query only reuses a previous
  // selection while the `select` identity holds, and grids mount this hook
  // once per row, so an inline selector re-maps the whole unit dictionary on
  // every render of every consumer.
  const select = useCallback(
    (data: UnitsResponse) => mapUnitOptions(data, label, filterFunction),
    [filterFunction, label],
  );

  const { data: options } = useQueryUnits<UnitSelectOption[]>(
    {
      language,
      unitType: typeof unitType === 'string' ? [unitType] : unitType,
    },
    { select },
  );

  return options ?? [];
};
